"use client";

import { motion, useReducedMotion } from "framer-motion";
import { useSyncExternalStore, type ReactNode } from "react";
import { cn } from "@/lib/utils";

const easeOut = [0.22, 1, 0.36, 1] as const;

type FadeInViewProps = {
  children: ReactNode;
  className?: string;
  triggerOnMount?: boolean;
  amount?: number;
  duration?: number;
  once?: boolean;
};

export function FadeInView({
  children,
  className,
  triggerOnMount = false,
  amount = 0,
  duration = 0.63,
  once = true,
}: FadeInViewProps) {
  const reduceMotion = useReducedMotion();
  const instant = Boolean(reduceMotion);
  const mounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );

  const hidden = { opacity: 0 };
  const visible = { opacity: 1 };

  if (!mounted) {
    return <div className={cn(className)}>{children}</div>;
  }

  if (instant) {
    return (
      <motion.div
        className={cn(className)}
        initial={false}
        animate={visible}
        transition={{ duration: 0 }}
      >
        {children}
      </motion.div>
    );
  }

  if (triggerOnMount) {
    return (
      <motion.div
        className={cn(className)}
        initial={hidden}
        animate={visible}
        transition={{
          duration,
          ease: easeOut,
        }}
      >
        {children}
      </motion.div>
    );
  }

  return (
    <motion.div
      className={cn(className)}
      initial={hidden}
      whileInView={visible}
      viewport={{ once, amount }}
      transition={{ duration, ease: easeOut }}
    >
      {children}
    </motion.div>
  );
}
